// MATERIAL UI
import { Skeleton, Stack, Typography } from "@mui/material";

// STYLES
import { BORDER_LIGHT, Card, NAVY, StatusPill, TEXT_MUTED, TEXT_SECONDARY } from "./styles";

interface TransactionReviewSummaryCardProps {
  needsReview: number;
  declared: number;
  taxYear: number;
  loading: boolean;
  error: boolean;
  onOpen: () => void;
}

const Stat = ({ value, label }: { value: React.ReactNode; label: string }) => (
  <Stack sx={{ pt: 1.75 }} spacing={0.75}>
    <Typography sx={{ fontSize: 22, fontWeight: 600, color: NAVY, lineHeight: 1 }}>
      {value}
    </Typography>
    <Typography sx={{ fontSize: 12, color: TEXT_MUTED, lineHeight: 1.4 }}>{label}</Typography>
  </Stack>
);

const TransactionReviewSummaryCard = ({
  needsReview,
  declared,
  taxYear,
  loading,
  error,
  onOpen,
}: TransactionReviewSummaryCardProps) => (
  <Card
    onClick={onOpen}
    sx={{
      p: 3,
      display: "flex",
      flexDirection: "column",
      gap: 1.75,
      height: "100%",
      cursor: "pointer",
      "&:hover": { borderColor: TEXT_MUTED },
    }}>
    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1.5}>
      <Typography sx={{ fontSize: 17, fontWeight: 600, color: NAVY, lineHeight: 1.3 }}>
        Transaction review
      </Typography>
      {loading ? (
        <Skeleton variant="rounded" width={96} height={24} />
      ) : error ? (
        <StatusPill tone="warning">Action required</StatusPill>
      ) : needsReview > 0 ? (
        <StatusPill tone="error">{`${needsReview} need${needsReview === 1 ? "s" : ""} review`}</StatusPill>
      ) : (
        <StatusPill tone="success">Up to date</StatusPill>
      )}
    </Stack>
    <Typography sx={{ fontSize: 14, color: TEXT_SECONDARY, lineHeight: 1.55 }}>
      Enter the acquisition date, quantity and cost of each deposit so your gains and 1099-DA are
      calculated correctly.
    </Typography>
    <Stack
      direction="row"
      gap={3.5}
      sx={{ mt: "auto", pt: 0.5, borderTop: `1px solid ${BORDER_LIGHT}` }}>
      <Stat value={loading ? <Skeleton width={28} /> : needsReview} label="Needs review" />
      <Stat value={loading ? <Skeleton width={28} /> : declared} label="Declared" />
      <Stat value={taxYear} label="Tax year (open)" />
    </Stack>
  </Card>
);

export default TransactionReviewSummaryCard;
